import { call, put, takeLatest, delay, select, fork } from 'redux-saga/effects';
import { callWithAuth } from '../sagaApiHelper';
import { getQrHistoryApi, getQrCountsApi, downloadQrCodesApi, downloadCsvApi, generateQrCodesApi, checkQrGenerationStatusApi, softHideQrRunApi } from '../../api/apiLists/qrApi';
import {
  loadQrHistoryRequest,
  loadQrCountsRequest,
  loadQrHistoryStart,
  loadQrHistorySuccess,
  loadQrHistoryFailure,
  downloadQrCodesRequest,
  downloadCsvRequest,
  downloadQrCodesStart,
  downloadQrCodesSuccess,
  downloadQrCodesFailure,
  downloadCsvStart,
  downloadCsvSuccess,
  downloadCsvFailure,
  loadQrCountsStart,
  loadQrCountsSuccess,
  loadQrCountsFailure,
  generateQrCodesRequest,
  generateQrCodesStart,
  generateQrCodesSuccess,
  generateQrCodesFailure,
  checkQrGenerationStatusStart,
  checkQrGenerationStatusSuccess,
  checkQrGenerationStatusFailure,
  stopPollingRequest,
  continuePollingRequest,
  stopPolling,
  continuePolling,
  softHideQrRunRequest,
  softHideQrRunStart,
  softHideQrRunSuccess,
  softHideQrRunFailure
} from '../qrSlice';
import { showAlert } from '../alertSlice';
import { downloadBase64File } from '../../utils/downloadFile';

function* handleLoadQrHistory(action) {
  try {
    yield put(loadQrHistoryStart());

    const incoming = action.payload || {};
    const limit = incoming.limit !== undefined ? Number(incoming.limit) : 10;
    
    // Handle offset-based pagination
    let offset = 0;
    const nextToken = incoming.nextToken || null;

    // Parse nextToken as offset
    if (nextToken !== null) {
      try {
        if (typeof nextToken === 'string' && !isNaN(nextToken)) {
          offset = Number(nextToken);
        } else if (typeof nextToken === 'number') {
          offset = nextToken;
        }
      } catch (e) {
        console.warn('Error parsing nextToken:', e);
        offset = incoming.offset !== undefined ? Number(incoming.offset) : 0;
      }
    } else {
      offset = incoming.offset !== undefined ? Number(incoming.offset) : 0;
    }

    const payload = { limit, offset };

    const res = yield call(callWithAuth, getQrHistoryApi, [payload]);

    if (res.status && res.data !== undefined) {
      let items = [];
      let responseNextToken = null;
      let count = 0;

      if (Array.isArray(res.data)) {
        items = res.data;
        // For array response, create nextToken as next offset
        if (items.length === limit) {
          responseNextToken = offset + limit;
        }
      } else if (res.data && res.data.items) {
        items = res.data.items || [];
        responseNextToken = res.data.nextToken || (items.length === limit ? offset + limit : null);
        count = res.data.count || res.data.total || 0;
      } else if (res.data && res.data.Items) {
        items = res.data.Items || [];
        responseNextToken = res.data.nextToken || (items.length === limit ? offset + limit : null);
        count = res.data.count || res.data.total || 0;
      } else if (res.data === null || (typeof res.data === 'object' && Object.keys(res.data).length === 0)) {
        // Handle empty response - likely requesting a page that doesn't exist
        items = [];
        responseNextToken = null;
        count = 0;
        
        // Log warning about empty page
        if (offset > 0) {
          console.warn(`No data found for offset ${offset}. This might be an invalid page request.`);
        }
      }

      yield put(loadQrHistorySuccess({ 
        history: items, 
        nextToken: responseNextToken,
        count: count
      }));
    } else {
      yield put(
        loadQrHistoryFailure({ error: res.errMsg || 'Failed to load QR history' })
      );
      yield put(
        showAlert({
          type: 'error',
          message: res.errMsg || 'Failed to load QR history'
        })
      );
    }
  } catch (err) {
    yield put(loadQrHistoryFailure({ error: err.message }));
    yield put(
      showAlert({
        type: 'error',
        message: err.message
      })
    );
  }
}

function* handleDownloadQrCodes(action) {
  try {
    yield put(downloadQrCodesStart());
    const { runId, s3Link, startSerialNo } = action.payload;

    const res = yield call(callWithAuth, downloadQrCodesApi, [{ runId, s3Link, startSerialNo }]);

    if (res.status && res.data?.presignedUrl) {
      // Backend returns presigned URL, open it in new window for direct S3 download
      window.open(res.data.presignedUrl, '_blank');
      yield put(downloadQrCodesSuccess());
      yield put(
        showAlert({
          type: 'success',
          message: 'QR codes download started'
        })
      );
    } else {
      yield put(
        downloadQrCodesFailure({ error: res.errMsg || 'Failed to download QR codes' })
      );
      yield put(
        showAlert({
          type: 'error',
          message: res.errMsg || 'Failed to download QR codes'
        })
      );
    }
  } catch (err) {
    yield put(downloadQrCodesFailure({ error: err.message }));
    yield put(
      showAlert({
        type: 'error',
        message: err.message
      })
    );
  }
}

function* handleLoadQrCounts(action) {
  try {
    yield put(loadQrCountsStart());
    const res = yield call(callWithAuth, getQrCountsApi, [{}]);
    
    if (res.status && res.data) {
      yield put(loadQrCountsSuccess({ 
        Total: res.data.Total || 0,
        Used: res.data.Used || 0,
        Hidden: res.data.Hidden || 0,
        period: res.data.period || 'all time'
      }));
    } else {
      yield put(loadQrCountsFailure({ error: res.errMsg || 'Failed to load QR counts' }));
    }
  } catch (err) {
    yield put(loadQrCountsFailure({ error: err.message }));
  }
}

function* handleDownloadCsv(action) {
  try {
    yield put(downloadCsvStart());
    const { submissionId, startSerialNo, createdDate } = action.payload;

    const res = yield call(callWithAuth, downloadCsvApi, [{ submissionId, startSerialNo }]);

    if (res.status && res.data) {
      const base64Data = res.data;
      const createdDateObj = new Date(createdDate);
      const formattedDate = createdDateObj.toLocaleDateString('en-US', { 
        month: 'short', 
        day: '2-digit', 
        year: 'numeric' 
      }).replace(/\s/g, '_').replace(',', '');
      const filename = `${startSerialNo}_${formattedDate}.xlsx`;

      yield call(downloadBase64File, base64Data, filename);
      yield put(downloadCsvSuccess());
      yield put(
        showAlert({
          type: 'success',
          message: 'Excel file downloaded successfully'
        })
      );
    } else {
      yield put(
        downloadCsvFailure({ error: res.errMsg || 'Failed to download Excel file' })
      );
      yield put(
        showAlert({
          type: 'error',
          message: res.errMsg || 'Failed to download Excel file'
        })
      );
    }
  } catch (err) {
    yield put(downloadCsvFailure({ error: err.message }));
    yield put(
      showAlert({
        type: 'error',
        message: err.message
      })
    );
  }
}

function* handleGenerateQrCodes(action) {
  try {
    yield put(generateQrCodesStart());
    const { skuId, quantity, qrCodeImageFormat, startSerialNo } = action.payload;
    // get current user info from auth slice
    const auth = yield select((state) => state.auth || {});
    const authUser = auth.user || {};
    const createdById = authUser.userId;
    const createdByName = authUser.email;
    const res = yield call(
      callWithAuth,
      generateQrCodesApi,
      [{ skuId, quantity, qrCodeImageFormat, startSerialNo, createdById, createdByName }]
    );

    if (res.status && res.data) {
      const submissionId = res.data.submissionId || res.data.runId;

      yield put(generateQrCodesSuccess({ submissionId, startSerialNo }));
      yield put(
        showAlert({
          type: 'success',
          message: 'QR code generation has been submitted. We\'ll check progress automatically.'
        })
      );

      // Start polling immediately
      yield fork(pollQrGenerationStatus, submissionId);

    } else if (res.statusCode === 409) {
      yield put(generateQrCodesFailure({ error: res.errMsg }));
      yield put(
        showAlert({
          type: 'error',
          message: res.errMsg
        })
      );
    } else {
      yield put(generateQrCodesFailure({ error: res.errMsg || 'Failed to generate QR codes' }));
      yield put(
        showAlert({
          type: 'error',
          message: res.errMsg || 'Failed to generate QR codes'
        })
      );
    }
  } catch (err) {
    yield put(generateQrCodesFailure({ error: err.message }));
    yield put(
      showAlert({
        type: 'error',
        message: err.message
      })
    );
  }
}

function* pollQrGenerationStatus(submissionId) {
  const MAX_ATTEMPTS = 30;
  const POLL_INTERVAL = 4000; // 20 seconds

  try {
    let attempts = 0;

    while (attempts < MAX_ATTEMPTS) {
      // Check if polling should stop
      const state = yield select((state) => state.qr);
      if (!state.polling || !state.currentJob) {
        return;
      }

      yield put(checkQrGenerationStatusStart());

      const res = yield call(callWithAuth, checkQrGenerationStatusApi, [{ submissionId }]);

      if (res.status && res.data) {
        const { status, s3Link } = res.data;

        yield put(checkQrGenerationStatusSuccess({ status, s3Link }));

        if (status === 'COMPLETED') {
          yield put(
            showAlert({
              type: 'success',
              message: 'Your QR codes are ready for download!'
            })
          );
          // Refresh history to show new entry
          yield put(loadQrHistoryRequest({}));
          return;
        } else if (status === 'FAILED') {
          yield put(
            showAlert({
              type: 'error',
              message: 'QR code generation failed. Please try again.'
            })
          );
          return;
        }

        // Status is still 'SUBMITTED', continue polling
        attempts++;

        if (attempts < MAX_ATTEMPTS) {
          yield delay(POLL_INTERVAL);
        }

      } else {
        // API error - continue polling for temporary errors
        yield put(checkQrGenerationStatusFailure({ error: res.errMsg || 'Failed to check status' }));
        attempts++;

        if (attempts < MAX_ATTEMPTS) {
          yield delay(POLL_INTERVAL);
        }
      }
    }

    // Max attempts reached
    yield put(stopPolling());
    yield put(
      showAlert({
        type: 'info',
        message: 'QR generation is taking longer than expected.'
      })
    );

  } catch (err) {
    yield put(checkQrGenerationStatusFailure({ error: err.message }));
    yield put(stopPolling());
  }
}

function* handleStopPolling() {
  yield put(stopPolling());
}

function* handleContinuePolling(action) {
  const state = yield select((state) => state.qr);
  if (state.currentJob && state.currentJob.submissionId) {
    yield put(continuePolling());
    yield fork(pollQrGenerationStatus, state.currentJob.submissionId);
  }
}

function* handleSoftHideQrRun(action) {
  try {
    yield put(softHideQrRunStart());
    const { runId } = action.payload;

    // Get current user info from auth slice
    const auth = yield select((state) => state.auth || {});
    const authUser = auth.user || {};
    const hiddenBy = authUser.userId;

    const res = yield call(callWithAuth, softHideQrRunApi, [{ runId, hiddenBy }]);

    if (res.status && res.data) {
      yield put(softHideQrRunSuccess({ runId }));
      yield put(
        showAlert({
          type: 'success',
          message: res.data.message || 'QR generation run hidden successfully'
        })
      );
    } else {
      yield put(
        softHideQrRunFailure({ error: res.errMsg || 'Failed to hide QR run' })
      );
      yield put(
        showAlert({
          type: 'error',
          message: res.errMsg || 'Failed to hide QR run'
        })
      );
    }
  } catch (err) {
    yield put(softHideQrRunFailure({ error: err.message }));
    yield put(
      showAlert({
        type: 'error',
        message: err.message
      })
    );
  }
}

export default function* qrSaga() {
  yield takeLatest(loadQrHistoryRequest.type, handleLoadQrHistory);
  yield takeLatest(loadQrCountsRequest.type, handleLoadQrCounts);
  yield takeLatest(downloadQrCodesRequest.type, handleDownloadQrCodes);
  yield takeLatest(downloadCsvRequest.type, handleDownloadCsv);
  yield takeLatest(generateQrCodesRequest.type, handleGenerateQrCodes);
  yield takeLatest(stopPollingRequest.type, handleStopPolling);
  yield takeLatest(continuePollingRequest.type, handleContinuePolling);
  yield takeLatest(softHideQrRunRequest.type, handleSoftHideQrRun);
}
